import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.browser.localStorage
import kotlinx.browser.window

@Serializable
data class JwtClaims(
    val sub: String? = null,        // user_id
    val email: String? = null,
    val exp: Long? = null,          // expirace (Unix sekundy)
)

/** `atob` is a browser global (base64 -> binary string); declared as external for Kotlin/JS interop. */
external fun atob(data: String): String

external fun decodeURIComponent(s: String): String

/** Výsledek načtení odkazu pro obnovu hesla z URL hashe. */
data class RecoveryLink(val accessToken: String?, val error: String?)

/**
 * GoTrue po ověření recovery odkazu (z e-mailu) přesměruje na app s tokeny v URL hashi:
 * `#access_token=<jwt>&refresh_token=...&type=recovery`. Při vypršelém/neplatném odkazu místo toho
 * vrací `#error=...&error_description=...`. Vrátí RecoveryLink pokud jde o recovery kontext, jinak null.
 */
fun recoveryFromUrl(): RecoveryLink? {
    val hash = window.location.hash.removePrefix("#")
    if (hash.isBlank()) return null
    val p = hash.split("&").mapNotNull { seg ->
        val i = seg.indexOf('='); if (i < 0) null else seg.substring(0, i) to seg.substring(i + 1)
    }.toMap()
    val err = p["error_description"] ?: p["error"]
    return when {
        p["type"] == "recovery" && p["access_token"] != null -> RecoveryLink(p["access_token"], null)
        err != null -> RecoveryLink(null, runCatching { decodeURIComponent(err.replace('+', ' ')) }.getOrDefault(err))
        else -> null
    }
}

/** Smaže tokeny/chybu z URL (aby v adrese nezůstaly a reload flow nespustil znovu). */
fun clearUrlHash() {
    try { window.history.replaceState(null, "", window.location.pathname) } catch (_: Throwable) {}
}

/** Načte sdílené read-only heslo z `?access=...` a ihned parametr odstraní z adresního řádku. */
fun takeReadOnlyAccessFromUrl(): String? {
    val query = window.location.search.removePrefix("?")
    if (query.isBlank()) return null
    val segments = query.split("&")
    var access: String? = null
    val remaining = segments.filter { segment ->
        val i = segment.indexOf('=')
        val rawKey = if (i < 0) segment else segment.substring(0, i)
        val key = runCatching { decodeURIComponent(rawKey.replace('+', ' ')) }.getOrDefault(rawKey)
        if (key == "access") {
            val rawValue = if (i < 0) "" else segment.substring(i + 1)
            access = runCatching { decodeURIComponent(rawValue.replace('+', ' ')) }.getOrDefault(rawValue)
            false
        } else true
    }
    if (access != null) {
        val rest = remaining.takeIf { it.isNotEmpty() }?.joinToString("&")?.let { "?$it" } ?: ""
        try { window.history.replaceState(null, "", window.location.pathname + rest + window.location.hash) } catch (_: Throwable) {}
    }
    return access?.takeIf { it.isNotBlank() }
}

private val jwtJson = Json { ignoreUnknownKeys = true }

/** Decodes the JWT payload client-side to read sub/email for display. Not a security boundary —
 *  RLS on the server enforces permissions; role (player/admin) comes from sg_member, not the JWT. */
fun decodeJwtClaims(token: String): JwtClaims? = try {
    val payload = token.split(".").getOrNull(1) ?: return null
    var normalized = payload.replace('-', '+').replace('_', '/')
    normalized += "=".repeat((4 - normalized.length % 4) % 4)
    jwtJson.decodeFromString<JwtClaims>(atob(normalized))
} catch (e: Throwable) {
    null
}

/** True, pokud token má exp claim a ten už vypršel (s malou rezervou). Neznámý/nedekódovatelný
 *  exp -> false (nechá rozhodnout server; případný 401 vyřeší reaktivní odhlášení). */
fun isTokenExpired(token: String, skewSeconds: Long = 10): Boolean {
    val exp = decodeJwtClaims(token)?.exp ?: return false
    val nowSec = (kotlin.js.Date().getTime() / 1000).toLong()
    return nowSec >= exp - skewSeconds
}

// Jednoduchá perzistence session tokenu přes reload (localStorage). Není to bezpečnostní hranice.
private const val TOKEN_KEY = "sg_session_token"

fun loadSessionToken(): String? = try { localStorage.getItem(TOKEN_KEY) } catch (_: Throwable) { null }

fun saveSessionToken(token: String?) {
    try {
        if (token == null) localStorage.removeItem(TOKEN_KEY) else localStorage.setItem(TOKEN_KEY, token)
    } catch (_: Throwable) { /* storage disabled -- ignore */ }
}
